// @flow
import "react-resizable/css/styles.css";

import FormHeaderTeam from "components/FormCustomer/FormHeaderTeam";
import UnauthorizedPage from "components/UnauthorizedPage";
import usePermissions from "hooks/usePermissions";
import type { UseSignatureMemo } from "hooks/useSignatureMemo";
import useSignatureMemo from "hooks/useSignatureMemo";
import PdfFillerContextProvider, {
  PDFFillerContext,
} from "pages/Contexts/PdfFillerContext";
import * as React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Helmet } from "react-helmet";
import { useHistory, useLocation } from "react-router-dom";
import type { ViewMode } from "types/pdf-filler-context";
import type { Response } from "types/response";
import type { TeamDetails } from "types/team_details";
import FullHeightContainer from "ui/atoms/FullHeightContainer";
import Page from "ui/organisms/Page";

import { OfficeBanner } from "@/components/OfficeBanner";
import BreadcrumbButton from "@/ui/atoms/BreadcrumbButton.jsx";

import ResponseCustomerSubmitted from "../customer/ResponseCustomerSubmitted.jsx";
import DocumentSection from "./Components/DocumentSection";
import ResponseZoomControl from "./Components/ResponseZoomControl";

type Props = {
  allowResubmit?: boolean,
  viewMode?: ViewMode,
  resourceId?: string,
  hideHeader?: boolean,
  hideFooter?: boolean,
  isPacket?: boolean,
  preloadedResponse?: ?Response,
  signatureMemo?: UseSignatureMemo,
  history?: {
    replace: (path: string) => void,
    go: (number) => void,
  },
  match?: {
    params: {
      resourceId: string,
      viewMode: ViewMode,
    },
  },
  isTablet?: boolean,
  isKiosk?: boolean,
  overrideSuccessModal?: () => void,
  response?: Response,
};

function renderHeader(
  teamDetails: ?TeamDetails,
  viewMode: ViewMode,
  isPacket: ?boolean,
) {
  const shouldNotRenderHeader =
    !teamDetails || !["fill", "preview"].includes(viewMode) || isPacket;

  if (shouldNotRenderHeader) {
    return null;
  }

  return <FormHeaderTeam team={teamDetails} />;
}

function PDFFiller({
  allowResubmit,
  hideHeader,
  hideFooter,
  isPacket,
  preloadedResponse,
  resourceId,
  viewMode: propViewMode,
  signatureMemo,
  history,
  match,
  isTablet,
  isKiosk,
  overrideSuccessModal,
  response,
}: Props): React.Node {
  const viewMode = propViewMode || match?.params?.viewMode;
  const localSignatureMemo = useSignatureMemo(match?.params?.viewMode);
  const localHistory = useHistory();
  const location = useLocation();

  const { canEditPacketForm, canViewResponse } = usePermissions();

  React.useEffect(() => {
    if (!history) return;
    if (!match) return;

    const { viewMode, resourceId } = match.params;

    if (!canEditPacketForm && viewMode === "edit") {
      history.replace(`/r/preview/${resourceId}`);
    }
  }, [viewMode, history, canEditPacketForm, match]);

  const packetResponse = response?.packet_response;

  const backPathId = packetResponse || response?.responses?.[0]?.id;

  const backPathResponseType = packetResponse ? "packet" : "form";

  const backPath = backPathId
    ? `/response/${backPathId}?response_type=${backPathResponseType}`
    : "/";

  if (match?.params.viewMode === "response" && !canViewResponse) {
    return <UnauthorizedPage />;
  }

  return (
    <FullHeightContainer $offset={viewMode === "preview" ? 0 : 105}>
      <PdfFillerContextProvider
        isKiosk={isKiosk}
        isTablet={isTablet}
        resourceId={resourceId}
        signatureMemo={signatureMemo || localSignatureMemo}
        viewMode={viewMode}
      >
        <Helmet>
          <meta
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
            name="viewport"
          />
        </Helmet>
        <PDFFillerContext.Consumer>
          {({ viewMode, isSubmitted, teamDetails, redirectUrl }) => {
            if (overrideSuccessModal && isSubmitted) {
              overrideSuccessModal();
            }

            if (
              !isKiosk &&
              !isTablet &&
              isSubmitted &&
              !["response", "office-use"].includes(viewMode) &&
              redirectUrl
            ) {
              setTimeout(() => {
                window.location.href = redirectUrl;
              }, 2000);
            }

            if (
              !overrideSuccessModal &&
              isSubmitted &&
              !["response", "office-use"].includes(viewMode) &&
              !allowResubmit
            ) {
              return (
                <ResponseCustomerSubmitted
                  history={history || localHistory}
                  location={location}
                  match={{ params: { responseId: resourceId || "" } }}
                />
              );
            }

            return (
              <>
                {!hideHeader && renderHeader(teamDetails, viewMode, isPacket)}
                {viewMode === "office-use" ? (
                  <OfficeBanner.Root>
                    <BreadcrumbButton
                      className="m-0 !ml-6"
                      onClick={() => history.push(backPath)}
                    >
                      Back
                    </BreadcrumbButton>
                    <OfficeBanner.Content>
                      <OfficeBanner.Icon />
                      You are completing office use questions
                    </OfficeBanner.Content>
                  </OfficeBanner.Root>
                ) : null}
                <Page fluid>
                  <DndProvider backend={HTML5Backend}>
                    <DocumentSection
                      hideFooter={hideFooter}
                      isPacket={isPacket}
                      preloadedResponse={preloadedResponse}
                    />
                  </DndProvider>
                </Page>
                {viewMode === "response" && <ResponseZoomControl />}
              </>
            );
          }}
        </PDFFillerContext.Consumer>
      </PdfFillerContextProvider>
    </FullHeightContainer>
  );
}

export default PDFFiller;
